import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import parse from 'html-react-parser';
import { BoxV2 as Box, Flex, Button } from 'portal-commons';
import { useAuthContext } from '@/contexts';
import { RootReducer } from '@/store/types';
import { S3_ASSETS_PATH } from '@/constants';

/**
 * CspRegistrationWelcome Component
 * The entry point for CSP registration. Shows a welcome message and an activation button.
 */
const CspRegistrationWelcome: React.FC = () => {
  const { profile } = useAuthContext();
  const history = useHistory();

  const cspData = useSelector(
    (state: RootReducer) => state.shareReducer.cspData
  );

  // Auto-redirect if user is already active
  useEffect(() => {
    if (profile?.status?.toLowerCase() === 'active') {
      history.push('/');
    }
  }, [profile, history]);

  // Fetch welcome content from Redux/CMS
  const welcomeData = useMemo(() => {
    return cspData?.find((item) => item.lookup_key === 'csp-welcome');
  }, [cspData]);

  const title = welcomeData?.title || 'Welcome to The Campaign Registry!';
  const text =
    welcomeData?.text ||
    'In order to register Brands and Campaigns you need to activate your CSP account.Please fill in the required information in the following pages to allow us to validate your application';

  const handleActivate = () => {
    history.push('/user/csp/create');
  };

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      data-testid="initiateCspAccount"
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          maxWidth: '650px',
          gap: 'xl',
        }}
      >
        {/* Welcome Block */}
        <Box
          sx={{
            width: 550,
            border: '1px solid #00797c',
            background: '#00797c1a',
            padding: 30,
            color: '#00797c',
            fontSize: 'H400',
          }}
        >
          <Box
            as="img"
            alt="welcome-illustration"
            src={`${S3_ASSETS_PATH}/images/intiate-csp.svg`}
            sx={{ width: '50px', height: 'auto', mb: 20 }}
          />

          <Box
            as="h4"
            sx={{
              lineHeight: '19px',
              fontWeight: 500,
              mb: 25,
            }}
          >
            {title}
          </Box>
          <Box
            sx={{
              width: '80%',
              fontWeight: 400,
              lineHeight: '24px',
              margin: '0 auto',
            }}
          >
            {parse(text)}
          </Box>
        </Box>

        <Button
          data-testid="initiateCspAccountActivateButton"
          onClick={handleActivate}
          variant="standard"
          style={{ minWidth: '200px', fontWeight: 400 }}
        >
          Activate CSP Account
        </Button>
      </Flex>
    </Box>
  );
};

export default CspRegistrationWelcome;
